import { Navigate, useLocation } from 'react-router-dom';

import { type AuthState, type User, useAuthStore } from '@/features/auth/model';

import { ENV } from '@/shared/config/env';
import { ROUTES } from '@/shared/config/routes';
import { logger } from '@/shared/lib/logger';
import { readCompletedWizardFlag } from '@/shared/lib/onboarding-flag';

export const Permission = {
  AUTH: 'auth',
  ACCOUNT_NOT_DELETED: 'account_not_deleted',
  ADMIN: 'admin',
  SFW_ONLY: 'sfw_only', // Only accessible when NSFW is false
  // Closed-beta gate — user.accountStatus must be 'approved'. Non-approved
  // (pending/rejected/suspended) get redirected to /pending. Platform admins
  // bypass even when not approved (see ProtectedRoute body for the carve-out).
  APPROVED: 'approved',
} as const;

export type Permission = (typeof Permission)[keyof typeof Permission];

type PermissionChecker = (user: User | null) => boolean;

const permissionCheckers: Record<Permission, PermissionChecker> = {
  [Permission.AUTH]: (user) => !!user,
  [Permission.ACCOUNT_NOT_DELETED]: (user) => !!user && !user.isDeleted,
  [Permission.ADMIN]: (user) => !!user && user.isAdmin === true,
  [Permission.SFW_ONLY]: () => !ENV.NSFW, // Only accessible when NSFW is false
  [Permission.APPROVED]: (user) => user?.accountStatus === 'approved',
};

interface ProtectedRouteProps {
  children: React.ReactNode;
  permissions?: Permission[];
}

export const ProtectedRoute = ({
  children,
  permissions = [Permission.AUTH],
}: ProtectedRouteProps) => {
  const user = useAuthStore((state: AuthState) => state.user);
  const isAuthenticated = useAuthStore((state: AuthState) => state.isAuthenticated);
  const isInitializing = useAuthStore((state: AuthState) => state.isInitializing);
  const location = useLocation();

  logger.auth.debug('ProtectedRoute check', {
    path: location.pathname,
    permissions,
    hasUser: !!user,
    isAuthenticated,
    isInitializing,
    userId: user?.id,
  });

  // Wait for auth initialization to complete before making routing decisions
  if (isInitializing) {
    logger.auth.debug('ProtectedRoute waiting for auth initialization');
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Platform admins are Prism ops staff — they don't need the customer
  // onboarding wizard, and forcing them through it is hostile every time we
  // SQL-patch a seeded admin. Skip the redirects below for them.
  const isPlatformAdmin = user?.platformRole === 'platform_admin';

  // Check each permission
  for (const permission of permissions) {
    const checker = permissionCheckers[permission];

    // For AUTH permission, check both user and authenticated state
    if (permission === Permission.AUTH && (!checker(user) || !isAuthenticated)) {
      logger.auth.debug('ProtectedRoute AUTH permission failed', {
        permission,
        hasUser: !!user,
        isAuthenticated,
        path: location.pathname,
        redirectingTo: ROUTES.AUTH.REGISTER,
      });
      return <Navigate to={ROUTES.AUTH.REGISTER} state={{ from: location }} replace />;
    }

    if (!checker(user)) {
      // AUTH permission already handled above
      if (permission === Permission.AUTH) {
        logger.auth.debug('ProtectedRoute AUTH permission failed (secondary check)', {
          permission,
          hasUser: !!user,
          path: location.pathname,
          redirectingTo: ROUTES.AUTH.REGISTER,
        });
        return <Navigate to={ROUTES.AUTH.REGISTER} state={{ from: location }} replace />;
      }

      // Closed-beta gate — pending/rejected/suspended users go to /pending,
      // not /home. Platform admins bypass (they don't need to be approved to
      // hit /admin/*). The startsWith(/pending) guard prevents a redirect
      // loop in case a route under /pending is ever marked APPROVED.
      if (permission === Permission.APPROVED) {
        if (isPlatformAdmin || location.pathname.startsWith(ROUTES.PENDING)) {
          continue;
        }
        logger.auth.debug('ProtectedRoute APPROVED permission failed — redirecting to /pending', {
          accountStatus: user?.accountStatus,
          path: location.pathname,
        });
        return <Navigate to={ROUTES.PENDING} replace />;
      }

      // Other permissions failed - redirect to home
      logger.auth.debug('ProtectedRoute permission failed', {
        permission,
        hasUser: !!user,
        path: location.pathname,
        redirectingTo: ROUTES.HOME,
      });
      return <Navigate to={ROUTES.HOME} replace />;
    }
  }

  logger.auth.debug('ProtectedRoute access granted', {
    path: location.pathname,
    permissions,
    userId: user?.id,
  });

  // Redirect to onboarding if user hasn't finished it yet (skip for /onboarding itself).
  // hasTeam is always true post-callback because auth.service.register() auto-creates a
  // team — the relevant signal for "still in funnel" is the absence of onboarded_at.
  //
  // Belt-and-suspenders: the localStorage `onboarding.completedWizard` flag is
  // set when the wizard finishes (the wizard's last step → finishWizard). If either the
  // server's onboarded_at OR that flag is set, we treat the wizard as done.
  // This stops the "404 back-button → wizard reset" loop the founder hit when
  // /me hadn't refetched yet but the user clearly already completed.
  // /pending is the destination for non-approved users — it must NOT trigger
  // the onboarding redirect below, or non-approved users land in a loop:
  //   /pending → ProtectedRoute redirects to /onboarding (no onboardedAt) →
  //   /onboarding's APPROVED gate redirects back to /pending → repeat.
  const isPendingHoldingPath = location.pathname.startsWith(ROUTES.PENDING);

  const wizardCompleted = !!user?.onboardedAt || readCompletedWizardFlag();
  if (
    user &&
    user.hasTeam &&
    !wizardCompleted &&
    !isPlatformAdmin &&
    !location.pathname.startsWith('/onboarding') &&
    !isPendingHoldingPath
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  // Fallback: handle the rare case where auto-team failed.
  if (
    user &&
    !user.hasTeam &&
    !isPlatformAdmin &&
    !location.pathname.startsWith('/onboarding') &&
    !isPendingHoldingPath
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};
